/**
 * Whether (and how) this build turns on Firebase App Check.
 *
 * App Check proves requests come from this app, not from someone reusing the
 * Firebase config. Google requires it for Firebase AI Logic from Nov 2, 2026.
 * The provider is reCAPTCHA Enterprise (Google calls it "Fraud Defense" now),
 * whose key lists only the real domains — so:
 *
 * - no site key in the build (staging, forks) → off, the app works as before;
 * - emulators → off, they don't check tokens;
 * - localhost against real Firebase (`npm run dev`) → on with a debug token,
 *   because reCAPTCHA rejects localhost. `true` makes the SDK print a token to
 *   the console; register it in Firebase Console → App Check → Manage debug
 *   tokens, then put it in VITE_APPCHECK_DEBUG_TOKEN to keep reusing it;
 * - a real domain → on, and never with a debug token (that would let anyone
 *   holding it skip the check).
 */

export interface AppCheckInputs {
  siteKey: string | undefined;
  isEmulator: boolean;
  hostname: string;
  debugToken?: string;
}

export type AppCheckDecision =
  | { enabled: false; reason: 'no-site-key' | 'emulator' }
  | { enabled: true; siteKey: string; debugToken: string | true | null };

const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1']);

export const decideAppCheck = ({ siteKey, isEmulator, hostname, debugToken }: AppCheckInputs): AppCheckDecision => {
  const key = siteKey?.trim();
  if (!key) return { enabled: false, reason: 'no-site-key' };
  if (isEmulator) return { enabled: false, reason: 'emulator' };
  if (!LOCAL_HOSTS.has(hostname)) return { enabled: true, siteKey: key, debugToken: null };
  return { enabled: true, siteKey: key, debugToken: debugToken?.trim() || true };
};
